import type React from 'react';
import {Paper, Stack, Typography} from '@mui/material';
import type {SettingsChange, SettingsGroup, SettingValue} from '../types/settingsTypes';
import {LOCALE} from '~/pages/category-profile/domain/locale';

const WHEN = new Intl.DateTimeFormat(LOCALE, {day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'});
const text = (v: SettingValue) => Array.isArray(v) ? v.join(', ') : String(v);

/** The latest settings changes: who changed what, from and to. */
export const SettingsHistory: React.FC<{history: SettingsChange[]; groups: SettingsGroup[]}> = ({history, groups}) => {
    const label = (key: string) => groups.flatMap(g => g.settings).find(s => s.key === key)?.label ?? key;
    return (
        <Paper variant="outlined" id="settings-history" sx={{p: 2, scrollMarginTop: 16}}>
            <Typography variant="h6" component="h2">Change history</Typography>
            {history.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No settings changed yet: everything runs on the defaults.</Typography>
            ) : (
                <Stack gap={1.5} sx={{mt: 1}}>
                    {history.map(h => (
                        <Stack key={`${h.at}-${h.by}`}>
                            <Typography variant="caption" color="text.secondary">{`${WHEN.format(new Date(h.at))} · ${h.by}`}</Typography>
                            {Object.entries(h.changes).map(([key, c]) => (
                                <Typography key={key} variant="body2">{`${label(key)}: ${text(c.from)} → ${text(c.to)}`}</Typography>
                            ))}
                        </Stack>
                    ))}
                </Stack>
            )}
        </Paper>
    );
};
